/**
 * Offset-paginated, server-searched tags hosted by one TagProvider via
 * `:PROVIDES`, PLUS the leaf `:Tag` entries reachable through this
 * provider's `:UDTInstance` HAS_MEMBER tree. Powers the flat-view
 * tag-provider browser panel on `/plant/compute/hosts/:computeId`.
 *
 * The unified row shape is a projected map (not a raw node) so both
 * `:Tag` and UDT-member leaves can share one schema:
 *   { id, _id, name, folder, data_type, ignition_tag_type, ... }
 * `ignition_tag_type` is `'UDTMember'` for synthesised UDT leaves so
 * the UI can paint a small "UDT" pill on those rows; client-side
 * Live reads use each runtime tag's persisted `tag_path`.
 *
 * Post-refactor: UDT-instance members are themselves `:Tag` nodes with
 * `ignition_tag_type='udt_member'` reached via
 * `(:UDTInstance)-[:HAS_MEMBER*1..8]->(:Tag)`. The bounded depth guards
 * against pathological cycles; real UDT nesting is only a few levels
 * deep. Each tag carries the canonical `[Provider]Folder/Tag` on
 * `.tag_path` so no client-side composition is needed.
 *
 * Search runs after the union (over both `name` and `folder`);
 * `totalTags` reflects the search-filtered set and is stable across
 * pages so the pager can render "Page X of Y".
 *
 * Params: `{ providerId: string; search: string | null; skip: number;
 *           pageSize: number }`.
 */
export const PLANT_PROVIDER_TAGS_CYPHER = `
              MATCH (tp:TagProvider {id: $providerId})

              CALL {
                WITH tp

                // ─── Branch A: top-level :Tag rows ────────────────
                OPTIONAL MATCH (tp)-[:PROVIDES]->(t:Tag)
                WITH tp, t
                WHERE t IS NOT NULL AND t.tag_path IS NOT NULL AND trim(t.tag_path) <> ''
                RETURN {
                  id: t.id,
                  _id: coalesce(t.id, ''),
                  name: coalesce(t.name, ''),
                  tag_path: t.tag_path,
                  folder: coalesce(t.folder, t.folder_name, ''),
                  data_type: t.data_type,
                  ignition_tag_type: coalesce(t.ignition_tag_type, 'Tag'),
                  member_type: t.member_type,
                  opc_server: t.opc_server,
                  opc_item_path: t.opc_item_path,
                  opc_node_id: t.opc_node_id,
                  eng_unit: t.eng_unit,
                  eng_low: t.eng_low,
                  eng_high: t.eng_high,
                  read_only: t.read_only,
                  history_enabled: t.history_enabled
                } AS row

                UNION ALL

                WITH tp
                // ─── Branch B: UDT-instance member leaves ─────────
                // Walk the materialised runtime tree from the
                // provider's UDT instances down to every leaf :Tag,
                // through any number of nested instances. Bounded depth
                // guards against a pathological cycle; real UDT nesting
                // is only a few levels deep.
                OPTIONAL MATCH (tp)-[:PROVIDES]->(:UDTInstance)-[:HAS_MEMBER*1..8]->(t:Tag)
                  WHERE t.ignition_tag_type = 'udt_member'
                    AND t.tag_path IS NOT NULL
                    AND trim(t.tag_path) <> ''
                WITH DISTINCT t
                WHERE t IS NOT NULL
                RETURN {
                  id: t.id,
                  _id: coalesce(t.id, ''),
                  // Provider-relative full path (falls back to the
                  // in-instance name for graphs not yet re-ingested
                  // with provider_path).
                  name: coalesce(t.provider_path, t.name, ''),
                  tag_path: t.tag_path,
                  folder: coalesce(t.folder, ''),
                  data_type: t.data_type,
                  member_type: t.member_type,
                  opc_server: t.opc_server,
                  opc_item_path: t.opc_item_path,
                  opc_node_id: t.opc_node_id,
                  ignition_tag_type: 'UDTMember'
                } AS row
              }

              WITH tp, collect(row) AS rawRows
              WITH tp,
                   [r IN rawRows WHERE
                     $search IS NULL
                     OR toLower(coalesce(r.name, '')) CONTAINS toLower($search)
                     OR toLower(coalesce(r.folder, '')) CONTAINS toLower($search)
                   ] AS filtered
              WITH tp, size(filtered) AS totalTags, filtered

              UNWIND (CASE WHEN size(filtered) = 0 THEN [null] ELSE filtered END) AS r
              WITH tp, totalTags, r
              ORDER BY toLower(coalesce(r.folder, '~~~~')), toLower(coalesce(r.name, ''))
              SKIP toInteger($skip)
              LIMIT toInteger($pageSize)
              WITH tp, totalTags, [x IN collect(r) WHERE x IS NOT NULL] AS pageRows

              RETURN tp, totalTags, pageRows AS tags
            `
